import React, {
  FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import {
  getTaskDetailsApi,
  uploadTaskFileApi,
  submitTaskResponseApi,
} from "../api/TaskActions";

type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "file"
  | "image"
  | "email"
  | "url"
  | "yesno"
  | "rating"
  | "select"
  | "multiselect"
  | "phone"
  | "gp"
  | "booth";

interface FormField {
  field_id: number;
  task_id: number;
  field_label: string;
  field_type: FieldType | string;
  field_order: number;
  is_required: boolean | number;
  options?: string[];
  options_json?: string | null;
}

interface AssignedFormDetailsData {
  task_id: number;
  task_title: string;
  task_description?: string | null;
  deadline?: string | null;
  allowed_responses?: number | null;
  audience_type?: string | null;
  status?: string | null;
  created_at?: string | null;
  created_by?: number | null;
  client_name?: string | null;
  logo_image?: string | null;
  form_fields?: FormField[];
  assignments?: {
    contacts?: number[];
    groups?: number[];
    designations?: number[];
  };
}

type AnswerValue =
  | string
  | number
  | string[]
  | File
  | null;

type AnswersState = Record<number, AnswerValue>;

const parseOptions = (
  field: FormField,
): string[] => {
  if (Array.isArray(field.options)) {
    return field.options.map(String);
  }

  if (!field.options_json) {
    return [];
  }

  try {
    const parsed = JSON.parse(
      field.options_json,
    );

    return Array.isArray(parsed)
      ? parsed.map(String)
      : [];
  } catch {
    return [];
  }
};

const formatDeadline = (
  deadline?: string | null,
) => {
  if (!deadline) {
    return "No deadline";
  }

  const date = new Date(deadline);

  if (Number.isNaN(date.getTime())) {
    return deadline;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AssignedFormDetails: React.FC = () => {
  const navigate = useNavigate();

  const { task_id } = useParams<{
    task_id: string;
  }>();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}",
      );
    } catch {
      return {};
    }
  }, []);

  const userId = Number(
    currentUser?.user_id || 0,
  );

  const taskId = Number(task_id || 0);

  const [form, setForm] =
    useState<AssignedFormDetailsData | null>(
      null,
    );

  const [answers, setAnswers] =
    useState<AnswersState>({});

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const [successMessage, setSuccessMessage] =
    useState("");

  const orderedFields = useMemo(
    () =>
      [...(form?.form_fields || [])].sort(
        (a, b) =>
          Number(a.field_order || 0) -
          Number(b.field_order || 0),
      ),
    [form],
  );

  const fetchFormDetails =
    useCallback(async () => {
      if (!userId) {
        navigate("/login", {
          replace: true,
        });

        return;
      }

      if (
        !Number.isInteger(taskId) ||
        taskId <= 0
      ) {
        setErrorMessage(
          "Invalid assigned form.",
        );
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        const result = await getTaskDetailsApi(taskId);

        if (!result?.success) {
          throw new Error(
            result?.message ||
              "Unable to load the form.",
          );
        }

        const responseForm =
          result?.data || null;

        if (!responseForm) {
          throw new Error(
            "Assigned form not found.",
          );
        }

        setForm(responseForm);
      } catch (error: any) {
        console.error(
          "Assigned form details error:",
          error,
        );

        setForm(null);

        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load the assigned form.",
        );
      } finally {
        setLoading(false);
      }
    }, [navigate, taskId, userId]);

  useEffect(() => {
    fetchFormDetails();
  }, [fetchFormDetails]);

  const updateAnswer = (
    fieldId: number,
    value: AnswerValue,
  ) => {
    setAnswers((current) => ({
      ...current,
      [fieldId]: value,
    }));

    setErrorMessage("");
    setSuccessMessage("");
  };

  const toggleMultiValue = (
    fieldId: number,
    option: string,
  ) => {
    const currentValue =
      answers[fieldId];

    const selectedValues =
      Array.isArray(currentValue)
        ? currentValue
        : [];

    const nextValues =
      selectedValues.includes(option)
        ? selectedValues.filter(
            (item) => item !== option,
          )
        : [...selectedValues, option];

    updateAnswer(
      fieldId,
      nextValues,
    );
  };

  const validateForm = () => {
    if (!form) {
      return "Assigned form is unavailable.";
    }

    if (
      form.deadline &&
      new Date(form.deadline).getTime() <
        Date.now()
    ) {
      return "This form deadline has passed.";
    }

    for (const field of orderedFields) {
      if (!Boolean(field.is_required)) {
        continue;
      }

      const value =
        answers[field.field_id];

      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return `${field.field_label} is required.`;
      }

      if (
        Array.isArray(value) &&
        value.length === 0
      ) {
        return `${field.field_label} is required.`;
      }
    }

    return null;
  };

  const uploadSelectedFile = async (
    field: FormField,
    file: File,
  ) => {
    const result = await uploadTaskFileApi({
      file,
      task_id: taskId,
      field_id: field.field_id,
      uploaded_by: userId,
    });

    if (!result?.success) {
      throw new Error(
        result?.message ||
          `Unable to upload ${field.field_label}.`,
      );
    }

    return (
      result?.data?.file_path ||
      null
    );
  };

  const buildAnswerPayload = async (
    field: FormField,
  ) => {
    const value =
      answers[field.field_id];

    const base = {
      field_id: field.field_id,
    };

    switch (field.field_type) {
      case "number":
      case "rating":
        return {
          ...base,
          answer_number:
            value === "" ||
            value === null ||
            value === undefined
              ? null
              : Number(value),
        };

      case "date":
        return {
          ...base,
          answer_date:
            value || null,
        };

      case "select":
      case "multiselect":
      case "yesno":
        return {
          ...base,
          selected_values:
            Array.isArray(value)
              ? value
              : value
                ? [String(value)]
                : [],
        };

      case "file":
      case "image": {
        let filePath: string | null =
          typeof value === "string"
            ? value
            : null;

        if (value instanceof File) {
          filePath =
            await uploadSelectedFile(
              field,
              value,
            );
        }

        return {
          ...base,
          answer_file: filePath,
        };
      }

      default:
        return {
          ...base,
          answer_text:
            value === null ||
            value === undefined
              ? null
              : String(value),
        };
    }
  };

  const submitForm = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    const validationError =
      validateForm();

    if (validationError) {
      setErrorMessage(
        validationError,
      );
      return;
    }

    try {
      setSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const answerPayload =
        await Promise.all(
          orderedFields.map(
            buildAnswerPayload,
          ),
        );

      const result = await submitTaskResponseApi(
        taskId,
        {
          user_id: userId,
          answers: answerPayload,
        },
      );

      if (!result?.success) {
        throw new Error(
          result?.message ||
            "Unable to submit the form.",
        );
      }

      setSuccessMessage(
        result?.message ||
          "Response submitted successfully.",
      );

      setAnswers({});

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      window.setTimeout(() => {
        navigate("/forms");
      }, 1400);
    } catch (error: any) {
      console.error(
        "Assigned form submission error:",
        error,
      );

      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit the form.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (
    field: FormField,
  ) => {
    const value =
      answers[field.field_id];

    const options =
      parseOptions(field);

    const inputClass =
      "assigned-detail-input";

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            rows={5}
            className={`${inputClass} assigned-detail-textarea`}
            placeholder={`Enter ${field.field_label.toLowerCase()}`}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={
              typeof value === "number" ||
              typeof value === "string"
                ? value
                : ""
            }
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
          />
        );

      case "email":
        return (
          <input
            type="email"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
            placeholder="example@email.com"
          />
        );

      case "url":
        return (
          <input
            type="url"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
            placeholder="https://example.com"
          />
        );

      case "phone":
        return (
          <input
            type="tel"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
            placeholder="Enter contact number"
          />
        );

      case "select":
      case "gp":
      case "booth":
        return (
          <div className="assigned-detail-select-wrap">
            <select
              value={String(value || "")}
              onChange={(event) =>
                updateAnswer(
                  field.field_id,
                  event.target.value,
                )
              }
              className={inputClass}
            >
              <option value="">
                Select an option
              </option>

              {options.map((option) => (
                <option
                  key={option}
                  value={option}
                >
                  {option}
                </option>
              ))}
            </select>

            <i className="feather-chevron-down" />
          </div>
        );

      case "multiselect":
        return (
          <div className="assigned-detail-options">
            {options.map((option) => {
              const selected =
                Array.isArray(value)
                  ? value.includes(option)
                  : false;

              return (
                <label
                  key={option}
                  className={`assigned-detail-option ${
                    selected
                      ? "selected"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      toggleMultiValue(
                        field.field_id,
                        option,
                      )
                    }
                  />

                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        );

      case "yesno": {
        const yesNoOptions =
          options.length > 0
            ? options
            : ["Yes", "No"];

        return (
          <div className="assigned-detail-options">
            {yesNoOptions.map(
              (option) => (
                <label
                  key={option}
                  className={`assigned-detail-option ${
                    value === option
                      ? "selected"
                      : ""
                  }`}
                >
                  <input
                    type="radio"
                    name={`field-${field.field_id}`}
                    checked={
                      value === option
                    }
                    onChange={() =>
                      updateAnswer(
                        field.field_id,
                        option,
                      )
                    }
                  />

                  <span>{option}</span>
                </label>
              ),
            )}
          </div>
        );
      }

      case "rating":
        return (
          <div className="assigned-detail-rating">
            {[1, 2, 3, 4, 5].map(
              (rating) => (
                <button
                  key={rating}
                  type="button"
                  className={
                    value === rating
                      ? "selected"
                      : ""
                  }
                  onClick={() =>
                    updateAnswer(
                      field.field_id,
                      rating,
                    )
                  }
                >
                  {rating}
                </button>
              ),
            )}
          </div>
        );

      case "file":
      case "image":
        return (
          <div className="assigned-detail-upload">
            <label className="assigned-detail-upload-box">
              <i
                className={
                  field.field_type ===
                  "image"
                    ? "feather-image"
                    : "feather-upload-cloud"
                }
              />

              <div>
                <strong>
                  {value instanceof File
                    ? value.name
                    : field.field_type ===
                        "image"
                      ? "Choose an image"
                      : "Choose a file"}
                </strong>

                <span>
                  Click to browse from your
                  device
                </span>
              </div>

              <input
                type="file"
                accept={
                  field.field_type ===
                  "image"
                    ? "image/*"
                    : undefined
                }
                onChange={(event) =>
                  updateAnswer(
                    field.field_id,
                    event.target
                      .files?.[0] ||
                      null,
                  )
                }
              />
            </label>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={String(value || "")}
            onChange={(event) =>
              updateAnswer(
                field.field_id,
                event.target.value,
              )
            }
            className={inputClass}
            placeholder={`Enter ${field.field_label.toLowerCase()}`}
          />
        );
    }
  };

  const isClosed =
    Boolean(
      form?.deadline &&
        new Date(
          form.deadline,
        ).getTime() < Date.now(),
    );

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <main className="main-content right-chat-active assigned-detail-main">
        <div className="middle-sidebar-bottom assigned-detail-middle">
          <div className="middle-sidebar-left assigned-detail-left">
            <div className="assigned-detail-wrapper">
              <button
                type="button"
                className="assigned-detail-back"
                onClick={() =>
                  navigate("/forms")
                }
              >
                <i className="feather-arrow-left" />
                Back to Assigned Forms
              </button>

              {loading ? (
                <section className="assigned-detail-state">
                  <div className="spinner-border text-primary" />
                  <p>Loading form...</p>
                </section>
              ) : !form ? (
                <section className="assigned-detail-state">
                  <div className="assigned-detail-state-icon error">
                    <i className="feather-alert-circle" />
                  </div>

                  <h2>
                    Form unavailable
                  </h2>

                  <p>
                    {errorMessage ||
                      "This form could not be found."}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      navigate("/forms")
                    }
                  >
                    Return to Forms
                  </button>
                </section>
              ) : (
                <>
                  <section className="assigned-detail-header">
                    <div className="assigned-detail-header-copy">
                      <span className="assigned-detail-badge">
                        Assigned Form
                      </span>

                      <h1>
                        {form.task_title}
                      </h1>

                      <p>
                        {form.task_description ||
                          "Complete the information requested below."}
                      </p>
                    </div>

                    <div className="assigned-detail-deadline">
                      <span className="assigned-detail-deadline-icon">
                        <i className="feather-calendar" />
                      </span>

                      <div>
                        <label>
                          Deadline
                        </label>

                        <strong>
                          {formatDeadline(
                            form.deadline,
                          )}
                        </strong>
                      </div>
                    </div>
                  </section>

                  {successMessage && (
                    <div className="assigned-detail-message success">
                      <i className="feather-check-circle" />

                      <div>
                        <strong>
                          Submitted successfully
                        </strong>

                        <span>
                          {successMessage}
                        </span>
                      </div>
                    </div>
                  )}

                  {errorMessage && (
                    <div className="assigned-detail-message error">
                      <i className="feather-alert-circle" />
                      <span>
                        {errorMessage}
                      </span>
                    </div>
                  )}

                  <form
                    className="assigned-detail-form"
                    onSubmit={submitForm}
                  >
                    <div className="assigned-detail-form-heading">
                      <div>
                        <span>
                          Form Questions
                        </span>

                        <h2>
                          Provide your response
                        </h2>

                        <p>
                          Fields marked with *
                          are required.
                        </p>
                      </div>

                      <div className="assigned-detail-progress">
                        <strong>
                          {
                            orderedFields.length
                          }
                        </strong>

                        <span>
                          {orderedFields.length ===
                          1
                            ? "Question"
                            : "Questions"}
                        </span>
                      </div>
                    </div>

                    <div className="assigned-detail-fields">
                      {orderedFields.map(
                        (field, index) => (
                          <section
                            key={
                              field.field_id
                            }
                            className="assigned-detail-field-card"
                          >
                            <div className="assigned-detail-field-number">
                              {index + 1}
                            </div>

                            <div className="assigned-detail-field-content">
                              <label className="assigned-detail-label">
                                {
                                  field.field_label
                                }

                                {Boolean(
                                  field.is_required,
                                ) && (
                                  <span>
                                    *
                                  </span>
                                )}
                              </label>

                              <div className="assigned-detail-control">
                                {renderField(
                                  field,
                                )}
                              </div>
                            </div>
                          </section>
                        ),
                      )}
                    </div>

                    <div className="assigned-detail-actions">
                      <div className="assigned-detail-security">
                        <i className="feather-lock" />

                        <span>
                          Your response is linked
                          securely to your member
                          account.
                        </span>
                      </div>

                      <div className="assigned-detail-buttons">
                        <button
                          type="button"
                          className="assigned-detail-cancel"
                          onClick={() =>
                            navigate("/forms")
                          }
                          disabled={
                            submitting
                          }
                        >
                          Cancel
                        </button>

                        <button
                          type="submit"
                          className="assigned-detail-submit"
                          disabled={
                            submitting ||
                            isClosed
                          }
                        >
                          {submitting ? (
                            <>
                              <span className="assigned-detail-button-spinner" />
                              Submitting...
                            </>
                          ) : isClosed ? (
                            "Form Closed"
                          ) : (
                            <>
                              Submit Response
                              <i className="feather-arrow-right" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Popupchat />
      <Appfooter />

      <style>{`
        .assigned-detail-main {
          min-height: 100vh;
          padding-top: 84px;
          padding-left: 300px;
          padding-right: 330px;
          background: #f5f7fb;
          overflow-x: hidden;
        }

        .assigned-detail-middle,
        .assigned-detail-left {
          width: 100%;
          max-width: 100%;
        }

        .assigned-detail-left {
          padding: 0 12px;
        }

        .assigned-detail-wrapper {
          width: 100%;
          max-width: 980px;
          margin: 0 auto;
          padding-bottom: 34px;
        }

        .assigned-detail-back {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 0;
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          transition: 0.2s ease;
        }

        .assigned-detail-back:hover {
          color: #2563eb;
          transform: translateX(-2px);
        }

        .assigned-detail-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          padding: 20px 22px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: linear-gradient(
            135deg,
            #ffffff 0%,
            #f8fbff 100%
          );
          box-shadow: 0 7px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-detail-header-copy {
          min-width: 0;
          flex: 1;
        }

        .assigned-detail-badge {
          display: inline-flex;
          padding: 6px 12px;
          margin-bottom: 9px;
          border-radius: 999px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 600;
        }

        .assigned-detail-header h1 {
          margin: 0;
          color: #111827;
          font-size: 27px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: -0.3px;
        }

        .assigned-detail-header p {
          max-width: 650px;
          margin: 7px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .assigned-detail-deadline {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 210px;
          padding: 11px 13px;
          border: 1px solid #e8eef8;
          border-radius: 15px;
          background: #ffffff;
        }

        .assigned-detail-deadline-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          flex-shrink: 0;
          border-radius: 12px;
          background: #fef2f2;
          color: #dc2626;
        }

        .assigned-detail-deadline label {
          display: block;
          margin-bottom: 3px;
          color: #94a3b8;
          font-size: 10px;
        }

        .assigned-detail-deadline strong {
          display: block;
          color: #334155;
          font-size: 11px;
          font-weight: 650;
          white-space: nowrap;
        }

        .assigned-detail-message {
          display: flex;
          align-items: center;
          gap: 11px;
          margin-top: 14px;
          padding: 13px 15px;
          border-radius: 15px;
          font-size: 13px;
        }

        .assigned-detail-message.success {
          border: 1px solid #a7f3d0;
          background: #ecfdf5;
          color: #047857;
        }

        .assigned-detail-message.error {
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #b91c1c;
        }

        .assigned-detail-message i {
          font-size: 18px;
          flex-shrink: 0;
        }

        .assigned-detail-message strong,
        .assigned-detail-message span {
          display: block;
        }

        .assigned-detail-message strong {
          margin-bottom: 2px;
        }

        .assigned-detail-form {
          margin-top: 14px;
          padding: 20px 22px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: #ffffff;
          box-shadow: 0 7px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-detail-form-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          padding-bottom: 17px;
          border-bottom: 1px solid #eef2f7;
        }

        .assigned-detail-form-heading > div:first-child > span {
          display: block;
          margin-bottom: 5px;
          color: #2563eb;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .assigned-detail-form-heading h2 {
          margin: 0;
          color: #111827;
          font-size: 21px;
          font-weight: 700;
        }

        .assigned-detail-form-heading p {
          margin: 5px 0 0;
          color: #64748b;
          font-size: 12px;
        }

        .assigned-detail-progress {
          min-width: 76px;
          padding: 9px 11px;
          border: 1px solid #edf2f7;
          border-radius: 13px;
          background: #f8fafc;
          text-align: center;
        }

        .assigned-detail-progress strong {
          display: block;
          color: #111827;
          font-size: 17px;
          line-height: 1;
        }

        .assigned-detail-progress span {
          display: block;
          margin-top: 4px;
          color: #64748b;
          font-size: 10px;
        }

        .assigned-detail-fields {
          display: flex;
          flex-direction: column;
          gap: 13px;
          margin-top: 17px;
        }

        .assigned-detail-field-card {
          display: grid;
          grid-template-columns: 36px minmax(0, 1fr);
          gap: 12px;
          padding: 15px;
          border: 1px solid #e8eef8;
          border-radius: 16px;
          background: #fbfcff;
          transition: 0.2s ease;
        }

        .assigned-detail-field-card:focus-within {
          border-color: #bfdbfe;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.06);
        }

        .assigned-detail-field-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 700;
        }

        .assigned-detail-field-content {
          min-width: 0;
        }

        .assigned-detail-label {
          display: block;
          margin-bottom: 9px;
          color: #334155;
          font-size: 13px;
          font-weight: 650;
        }

        .assigned-detail-label span {
          margin-left: 3px;
          color: #dc2626;
        }

        .assigned-detail-control {
          min-width: 0;
        }

        .assigned-detail-input {
          width: 100%;
          min-height: 44px;
          padding: 0 13px;
          border: 1px solid #dbe7fb;
          border-radius: 12px;
          outline: none;
          background: #ffffff;
          color: #111827;
          font-size: 13px;
          transition: 0.2s ease;
        }

        .assigned-detail-input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
        }

        .assigned-detail-textarea {
          min-height: 110px;
          padding-top: 11px;
          padding-bottom: 11px;
          resize: vertical;
        }

        .assigned-detail-select-wrap {
          position: relative;
        }

        .assigned-detail-select-wrap select {
          appearance: none;
          padding-right: 38px;
        }

        .assigned-detail-select-wrap > i {
          position: absolute;
          top: 50%;
          right: 14px;
          transform: translateY(-50%);
          color: #64748b;
          pointer-events: none;
        }

        .assigned-detail-options {
          display: flex;
          flex-wrap: wrap;
          gap: 9px;
        }

        .assigned-detail-option {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 40px;
          padding: 0 13px;
          border: 1px solid #dbe7fb;
          border-radius: 11px;
          background: #ffffff;
          color: #475569;
          font-size: 12px;
          font-weight: 550;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .assigned-detail-option:hover,
        .assigned-detail-option.selected {
          border-color: #93c5fd;
          background: #eff6ff;
          color: #1d4ed8;
        }

        .assigned-detail-option input {
          accent-color: #2563eb;
        }

        .assigned-detail-rating {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .assigned-detail-rating button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border: 1px solid #dbe7fb;
          border-radius: 11px;
          background: #ffffff;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
          transition: 0.2s ease;
        }

        .assigned-detail-rating button:hover,
        .assigned-detail-rating button.selected {
          border-color: #2563eb;
          background: #2563eb;
          color: #ffffff;
        }

        .assigned-detail-upload-box {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 72px;
          padding: 13px 15px;
          border: 1px dashed #93c5fd;
          border-radius: 13px;
          background: #f8fbff;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .assigned-detail-upload-box:hover {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .assigned-detail-upload-box > i {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 39px;
          height: 39px;
          flex-shrink: 0;
          border-radius: 11px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 18px;
        }

        .assigned-detail-upload-box strong,
        .assigned-detail-upload-box span {
          display: block;
        }

        .assigned-detail-upload-box strong {
          color: #334155;
          font-size: 12px;
        }

        .assigned-detail-upload-box span {
          margin-top: 3px;
          color: #94a3b8;
          font-size: 10px;
        }

        .assigned-detail-upload-box input {
          display: none;
        }

        .assigned-detail-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-top: 18px;
          padding-top: 17px;
          border-top: 1px solid #eef2f7;
        }

        .assigned-detail-security {
          display: flex;
          align-items: center;
          gap: 7px;
          color: #94a3b8;
          font-size: 11px;
        }

        .assigned-detail-buttons {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .assigned-detail-cancel,
        .assigned-detail-submit,
        .assigned-detail-state button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 42px;
          padding: 0 18px;
          border-radius: 13px;
          font-size: 12px;
          font-weight: 650;
          transition: 0.2s ease;
        }

        .assigned-detail-cancel {
          border: 1px solid #dbe7fb;
          background: #ffffff;
          color: #475569;
        }

        .assigned-detail-cancel:hover {
          background: #f8fafc;
        }

        .assigned-detail-submit,
        .assigned-detail-state button {
          border: none;
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 8px 16px rgba(37, 99, 235, 0.15);
        }

        .assigned-detail-submit:hover,
        .assigned-detail-state button:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        .assigned-detail-cancel:disabled,
        .assigned-detail-submit:disabled {
          cursor: not-allowed;
          opacity: 0.55;
          transform: none;
        }

        .assigned-detail-button-spinner {
          width: 15px;
          height: 15px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: assignedDetailSpin 0.75s linear infinite;
        }

        .assigned-detail-state {
          padding: 54px 20px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: #ffffff;
          text-align: center;
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-detail-state p {
          margin: 12px auto 0;
          max-width: 470px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .assigned-detail-state h2 {
          margin: 12px 0 0;
          color: #111827;
          font-size: 21px;
        }

        .assigned-detail-state button {
          margin-top: 18px;
        }

        .assigned-detail-state-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 62px;
          height: 62px;
          margin: 0 auto;
          border-radius: 18px;
          font-size: 26px;
        }

        .assigned-detail-state-icon.error {
          background: #fef2f2;
          color: #dc2626;
        }

        @keyframes assignedDetailSpin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 1200px) {
          .assigned-detail-main {
            padding-top: 78px;
            padding-right: 18px;
            padding-left: 24px;
          }

          .assigned-detail-left {
            padding: 0;
          }

          .assigned-detail-wrapper {
            max-width: 100%;
          }
        }

        @media (max-width: 991px) {
          .assigned-detail-main {
            padding-top: 72px;
            padding-right: 16px;
            padding-left: 16px;
          }

          .assigned-detail-header {
            flex-direction: column;
          }

          .assigned-detail-deadline {
            width: 100%;
          }
        }

        @media (max-width: 575px) {
          .assigned-detail-main {
            padding-top: 16px;
            padding-right: 12px;
            padding-left: 12px;
          }

          .assigned-detail-header,
          .assigned-detail-form {
            padding: 15px;
            border-radius: 17px;
          }

          .assigned-detail-header h1 {
            font-size: 23px;
          }

          .assigned-detail-form-heading,
          .assigned-detail-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .assigned-detail-progress {
            width: 100%;
          }

          .assigned-detail-field-card {
            grid-template-columns: 1fr;
          }

          .assigned-detail-field-number {
            width: 30px;
            height: 30px;
          }

          .assigned-detail-security {
            justify-content: center;
            text-align: center;
          }

          .assigned-detail-buttons {
            width: 100%;
          }

          .assigned-detail-cancel,
          .assigned-detail-submit {
            flex: 1;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default AssignedFormDetails;
